import { Button, Combobox, FAIcon, toast } from '@/components'
import { ExportFormat } from '@/modules/exports'
import { useExportFormatOptions } from '@/modules/exports/hooks'
import { useMutation } from '@apollo/client'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useState } from 'react'
import { EXPORT_SALARY_ITEM_GENERATION_PREVIEW_MUTATION } from './mutations'

/** The salary export builds lines only for these; the flat and salary PDF
 * layouts belong to other exports. */
const UNSUPPORTED_FORMATS = [ExportFormat.EXCEL_FLAT, ExportFormat.PDF_SALARY]

/** Export what the rules would generate for every driver in a salary month. */
export const PreviewExport: React.FC<{ year: number; month: number }> = ({
  year,
  month,
}) => {
  const texts = useTranslate({
    title: 'settings.generation-preview.export-title',
    description: 'settings.generation-preview.export-description',
    format: 'common.export-format',
    export: 'settings.generation-preview.export-all',
    busy: 'exports.export-timeout',
  })
  const options = useExportFormatOptions(UNSUPPORTED_FORMATS)
  const [exportFormat, setExportFormat] = useState(ExportFormat.EXCEL)
  const [exportPreview, { loading }] = useMutation(
    EXPORT_SALARY_ITEM_GENERATION_PREVIEW_MUTATION
  )

  async function handleExport() {
    const { data } = await exportPreview({
      variables: { year, month, exportFormat },
    })
    const result = data?.exportSalaryItemGenerationPreview
    if (result?.ok) toast.export(result.exportId ?? null)
    else if (result?.exportTimeout) toast.error(texts.busy)
  }

  return (
    <section className="overflow-hidden rounded-2xl bg-white shadow-card">
      <div className="space-y-2 px-5 py-4">
        <h2 className="text-lg font-semibold text-stone-900">{texts.title}</h2>
        <p className="text-sm text-stone-600">{texts.description}</p>
      </div>
      <div className="grid gap-4 border-t border-stone-200 px-5 py-4 sm:grid-cols-2 sm:items-end">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase text-stone-500">
            {texts.format}
          </span>
          <Combobox
            mode="single"
            noSearch
            value={exportFormat}
            items={options}
            onChange={value => setExportFormat(value as ExportFormat)}
          />
        </label>
        <Button
          className="px-6 text-base sm:justify-self-end"
          disabled={loading}
          onClick={() => void handleExport()}
        >
          <FAIcon icon="download" />
          {texts.export}
        </Button>
      </div>
    </section>
  )
}
